import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  IconSearch,
  IconMinus,
  IconPlus,
  IconTrash,
  IconCash,
  IconCreditCard,
  IconQrcode,
  IconBuildingBank,
  IconUserPlus,
  IconCircleCheck,
  IconPrinter,
  IconBrandWhatsapp,
  IconMail,
  IconAlertTriangle,
  IconBackspace,
  IconPencil,
  IconCheck,
} from "@tabler/icons-react";
import "./checkout.scss";
import { useClinicData } from "../../context/ClinicDataContext";
import { PANELS, PAYMENT_METHODS } from "../../data/mockData";
import { BASE_PATH } from "../../components/AppLayout/AppLayout";
import PatientPicker from "../../components/PatientPicker/PatientPicker";
import NewPatientModal from "../../components/NewPatientModal/NewPatientModal";
import { formatDateLong, formatRM, formatTime, initials } from "../../utils/format";
import { formatPhone, whatsappNumber } from "../../utils/phone";

const METHOD_ICONS = {
  Cash: IconCash,
  Card: IconCreditCard,
  "DuitNow QR": IconQrcode,
  "Bank transfer": IconBuildingBank,
};

const round2 = (n) => Math.round(n * 100) / 100;

const CheckoutPage = () => {
  const [view, setView] = useState("counter");

  return (
    <div className="checkout-page">
      <div className="toolbar checkout-page__no-print">
        <div className="tabs" role="tablist" aria-label="Checkout views">
          <button
            type="button"
            role="tab"
            aria-selected={view === "counter"}
            className={`tabs__item ${view === "counter" ? "tabs__item--active" : ""}`}
            onClick={() => setView("counter")}
          >
            Counter
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "closing"}
            className={`tabs__item ${view === "closing" ? "tabs__item--active" : ""}`}
            onClick={() => setView("closing")}
          >
            Day closing
          </button>
        </div>
      </div>
      {view === "counter" ? <Counter /> : <DayClosing />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Counter
// ---------------------------------------------------------------------------

const Counter = () => {
  const {
    today,
    appointments,
    patients,
    patientById,
    apptTreatments,
    apptLabel,
    apptPrice,
    dentistById,
    treatments,
    products,
    createSale,
  } = useClinicData();
  const [params, setParams] = useSearchParams();

  // Patients who are done in the chair today and haven't paid yet.
  const queue = useMemo(
    () =>
      appointments
        .filter((a) => a.date === today && !a.checkedOut && ["completed", "in-treatment"].includes(a.status))
        .sort((x, y) => x.start.localeCompare(y.start)),
    [appointments, today]
  );
  const ready = queue.filter((a) => a.status === "completed");
  const inChair = queue.filter((a) => a.status === "in-treatment");

  const requested = params.get("appt");
  const [selection, setSelection] = useState(() => requested || ready[0]?.id || "walk-in");
  const [walkInPatientId, setWalkInPatientId] = useState("");
  const [registerQuery, setRegisterQuery] = useState(null); // null = closed; string = prefill from the search box
  const [cart, setCart] = useState([]);
  const [catalogTab, setCatalogTab] = useState("treatments");
  const [catalogQuery, setCatalogQuery] = useState("");
  const [discountInput, setDiscountInput] = useState("");
  const [discountType, setDiscountType] = useState("rm");
  const [panelOn, setPanelOn] = useState(false);
  const [panelName, setPanelName] = useState(PANELS[0]);
  const [panelInput, setPanelInput] = useState("");
  const [payments, setPayments] = useState([]);
  const [method, setMethod] = useState("Cash");
  const [amountInput, setAmountInput] = useState("");
  const [reference, setReference] = useState("");
  const [lastChange, setLastChange] = useState(0);
  const [completed, setCompleted] = useState(null);
  const [error, setError] = useState("");

  const appointment = appointments.find((a) => a.id === selection);
  const patientId = appointment ? appointment.patientId : walkInPatientId;
  const patient = patientById[patientId];

  // Load the visit's treatment into the cart whenever the selected patient changes.
  useEffect(() => {
    const appt = appointments.find((a) => a.id === selection);
    setCart(
      appt
        ? apptTreatments(appt).map((t) => ({
            key: `t-${t.id}`,
            treatmentId: t.id,
            name: t.name,
            price: t.price,
            listPrice: t.price,
            qty: 1,
            fromVisit: true,
          }))
        : []
    );
    setPayments([]);
    setDiscountInput("");
    setPanelOn(false);
    setPanelInput("");
    setAmountInput("");
    setReference("");
    setLastChange(0);
    setError("");
    // Only reset when the selection itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  const choose = (id) => {
    setCompleted(null);
    setSelection(id);
    setParams(id === "walk-in" ? {} : { appt: id });
  };

  // ----- Totals -----
  const subtotal = cart.reduce((sum, line) => sum + line.price * line.qty, 0);
  const discountValue = Number(discountInput) || 0;
  const discount = round2(Math.min(discountType === "pct" ? (subtotal * discountValue) / 100 : discountValue, subtotal));
  const panelAmount = panelOn ? round2(Math.min(Number(panelInput) || 0, subtotal - discount)) : 0;
  const patientPays = round2(subtotal - discount - panelAmount);
  const paidSoFar = round2(payments.reduce((sum, p) => sum + p.amount, 0));
  const remaining = round2(Math.max(patientPays - paidSoFar, 0));

  // Keep the amount field in step with what's left to pay for non-cash methods.
  useEffect(() => {
    if (method !== "Cash") setAmountInput(remaining ? remaining.toFixed(2) : "");
  }, [method, remaining]);

  // ----- Cart actions -----
  const addLine = (item, kind) => {
    setError("");
    setCart((lines) => {
      const key = `${kind}-${item.id}`;
      const existing = lines.find((l) => l.key === key);
      if (existing) return lines.map((l) => (l.key === key ? { ...l, qty: l.qty + 1 } : l));
      const line = { key, name: item.name, price: item.price, listPrice: item.price, qty: 1 };
      if (kind === "t") line.treatmentId = item.id;
      else line.productId = item.id;
      return [...lines, line];
    });
  };

  const changeQty = (key, delta) =>
    setCart((lines) =>
      lines.map((l) => (l.key === key ? { ...l, qty: l.qty + delta } : l)).filter((l) => l.qty > 0)
    );

  const removeLine = (key) => setCart((lines) => lines.filter((l) => l.key !== key));

  // Per-line price override, e.g. when the dentist agrees a lower price for this visit.
  const [editingKey, setEditingKey] = useState(null);
  const [priceDraft, setPriceDraft] = useState("");

  const startPriceEdit = (line) => {
    setEditingKey(line.key);
    setPriceDraft(String(line.price));
  };

  const commitPrice = () => {
    const value = round2(Number(priceDraft));
    if (priceDraft !== "" && value >= 0) {
      setCart((lines) => lines.map((l) => (l.key === editingKey ? { ...l, price: value } : l)));
    }
    setEditingKey(null);
  };

  const resetPrice = (key) =>
    setCart((lines) => lines.map((l) => (l.key === key ? { ...l, price: l.listPrice } : l)));

  // ----- Payment actions -----
  const keypad = (key) => {
    setAmountInput((value) => {
      if (key === "back") return value.slice(0, -1);
      if (key === "." && value.includes(".")) return value;
      if (value.includes(".") && value.split(".")[1].length >= 2) return value;
      return value === "0" && key !== "." ? key : value + key;
    });
  };

  const cashSuggestions = useMemo(() => {
    if (!remaining) return [];
    const options = [remaining];
    const candidates = [10, 50, 100].map((step) => Math.ceil(remaining / step) * step);
    candidates.push(Math.ceil(remaining / 100) * 100 + 100);
    candidates.forEach((value) => {
      if (value > remaining && !options.includes(value)) options.push(value);
    });
    return options.slice(0, 4);
  }, [remaining]);

  const takePayment = () => {
    const amount = round2(Number(amountInput) || 0);
    if (!cart.length) {
      setError("Add at least one item before taking payment.");
      return;
    }
    if (amount <= 0) {
      setError("Enter the amount received.");
      return;
    }
    if (method !== "Cash" && amount > remaining) {
      setError(`${method} payments can't be more than the ${formatRM(remaining)} still due.`);
      return;
    }
    const applied = Math.min(amount, remaining);
    setLastChange(method === "Cash" ? round2(amount - applied) : 0);
    setPayments((list) => [...list, { method, amount: applied, ref: reference.trim(), received: amount }]);
    setAmountInput("");
    setReference("");
    setError("");
  };

  const finish = () => {
    if (!cart.length) {
      setError("Add at least one item before completing checkout.");
      return;
    }
    const invoice = createSale({
      patientId: patientId || null,
      appointmentId: appointment?.id,
      items: cart.map(({ treatmentId, productId, name, price, listPrice, qty }) => ({
        treatmentId,
        productId,
        name,
        price,
        listPrice,
        qty,
      })),
      discount,
      panel: panelAmount ? { name: panelName, amount: panelAmount } : null,
      payments: payments.map(({ method: m, amount, ref }) => ({ method: m, amount, ref })),
    });
    setCompleted({
      invoice,
      patient,
      lines: cart,
      subtotal,
      discount,
      panelAmount,
      panelName,
      patientPays,
      change: lastChange,
      balance: remaining,
    });
  };

  const nextPatient = () => {
    setCompleted(null);
    const next = ready.find((a) => a.id !== selection);
    choose(next ? next.id : "walk-in");
  };

  // ----- Catalog -----
  const q = catalogQuery.trim().toLowerCase();
  const catalog = (catalogTab === "treatments" ? treatments : products).filter((i) => i.name.toLowerCase().includes(q));

  return (
    <div className="counter">
      {/* Queue */}
      <aside className="counter__queue checkout-page__no-print" aria-label="Patients to check out">
        <h2 className="counter__heading">Ready to pay</h2>
        <div className="counter__queue-list">
          {ready.map((a) => {
            const p = patientById[a.patientId];
            return (
              <button
                key={a.id}
                type="button"
                className={`queue-card ${selection === a.id ? "queue-card--active" : ""}`}
                aria-pressed={selection === a.id}
                onClick={() => choose(a.id)}
              >
                <span className="avatar">{initials(p.name)}</span>
                <span className="queue-card__text">
                  <strong>{p.name}</strong>
                  <span>
                    {apptLabel(a)}, {formatRM(apptPrice(a))}
                  </span>
                </span>
              </button>
            );
          })}
          {ready.length === 0 && <p className="counter__empty">Nobody is waiting to pay.</p>}

          {inChair.length > 0 && (
            <>
              <h3 className="counter__subheading">Still in the chair</h3>
              {inChair.map((a) => (
                <div className="queue-card queue-card--pending" key={a.id}>
                  <span className="avatar">{initials(patientById[a.patientId].name)}</span>
                  <span className="queue-card__text">
                    <strong>{patientById[a.patientId].name}</strong>
                    <span>Booked {formatTime(a.start)}</span>
                  </span>
                </div>
              ))}
            </>
          )}

          <button
            type="button"
            className={`queue-card queue-card--walkin ${selection === "walk-in" ? "queue-card--active" : ""}`}
            aria-pressed={selection === "walk-in"}
            onClick={() => choose("walk-in")}
          >
            <span className="avatar">
              <IconUserPlus size={18} stroke={1.75} />
            </span>
            <span className="queue-card__text">
              <strong>Walk-in sale</strong>
              <span>Products or treatments without a booking</span>
            </span>
          </button>
        </div>
      </aside>

      {completed ? (
        <Receipt data={completed} onNext={nextPatient} />
      ) : (
        <>
          {/* Cart */}
          <section className="counter__cart" aria-label="Bill">
            <header className="bill-head">
              {appointment ? (
                <>
                  <span className="avatar avatar--md">{initials(patient.name)}</span>
                  <div className="bill-head__text">
                    <h2>{patient.name}</h2>
                    <p>
                      {formatPhone(patient.phone)}, visit with {dentistById[appointment.dentistId]?.name} at {formatTime(appointment.start)}
                    </p>
                  </div>
                  <Link className="btn btn--ghost btn--sm" to={`${BASE_PATH}/patients/${patient.id}`}>
                    Patient record
                  </Link>
                </>
              ) : (
                <div className="bill-head__walkin">
                  <PatientPicker
                    patients={patients}
                    value={walkInPatientId}
                    onChange={setWalkInPatientId}
                    label="Customer"
                    placeholder="Search name, phone or IC no."
                    emptyLabel="Leave blank to sell to a walk-in customer without a record."
                    onRegister={(q) => setRegisterQuery(q)}
                  />
                </div>
              )}
            </header>

            {patient?.alerts.length > 0 && (
              <p className="bill-head__alert">
                <IconAlertTriangle size={16} stroke={2} />
                {patient.alerts.join(", ")}
              </p>
            )}

            <ul className="bill-lines">
              {cart.map((line) => (
                <li className="bill-lines__item" key={line.key}>
                  <div className="bill-lines__name">
                    <strong>{line.name}</strong>
                    {editingKey === line.key ? (
                      <span className="price-edit">
                        <label className="visually-hidden" htmlFor={`price-${line.key}`}>
                          Price each for {line.name} (RM)
                        </label>
                        <span className="price-edit__prefix">RM</span>
                        <input
                          id={`price-${line.key}`}
                          className="form-field__control price-edit__input"
                          type="number"
                          min="0"
                          step="0.01"
                          inputMode="decimal"
                          autoFocus
                          value={priceDraft}
                          onChange={(e) => setPriceDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitPrice();
                            if (e.key === "Escape") setEditingKey(null);
                          }}
                        />
                        <button className="btn btn--primary btn--icon price-edit__done" type="button" aria-label="Save price" onClick={commitPrice}>
                          <IconCheck size={18} stroke={2} />
                        </button>
                      </span>
                    ) : (
                      <span className="bill-lines__price-row">
                        <button
                          type="button"
                          className={`bill-lines__price ${line.price !== line.listPrice ? "bill-lines__price--adjusted" : ""}`}
                          aria-label={`Change price of ${line.name}, currently ${formatRM(line.price)} each`}
                          onClick={() => startPriceEdit(line)}
                        >
                          {formatRM(line.price)} each
                          <IconPencil size={14} stroke={1.75} />
                        </button>
                        {line.price !== line.listPrice && (
                          <>
                            <s className="bill-lines__was">{formatRM(line.listPrice)}</s>
                            <button type="button" className="bill-lines__reset" onClick={() => resetPrice(line.key)}>
                              Reset
                            </button>
                          </>
                        )}
                        {line.fromVisit && line.price === line.listPrice && (
                          <span className="bill-lines__note">from today's visit</span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="stepper" role="group" aria-label={`Quantity of ${line.name}`}>
                    <button type="button" aria-label="Decrease quantity" onClick={() => changeQty(line.key, -1)}>
                      <IconMinus size={18} stroke={2} />
                    </button>
                    <span aria-live="polite">{line.qty}</span>
                    <button type="button" aria-label="Increase quantity" onClick={() => changeQty(line.key, 1)}>
                      <IconPlus size={18} stroke={2} />
                    </button>
                  </div>
                  <strong className="bill-lines__total">{formatRM(line.price * line.qty)}</strong>
                  <button
                    type="button"
                    className="btn btn--ghost btn--icon bill-lines__remove"
                    aria-label={`Remove ${line.name}`}
                    onClick={() => removeLine(line.key)}
                  >
                    <IconTrash size={18} stroke={1.75} />
                  </button>
                </li>
              ))}
              {cart.length === 0 && <li className="bill-lines__empty">Tap an item below to add it to the bill.</li>}
            </ul>

            <div className="catalog">
              <div className="catalog__bar">
                <div className="tabs" role="tablist" aria-label="Item type">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={catalogTab === "treatments"}
                    className={`tabs__item ${catalogTab === "treatments" ? "tabs__item--active" : ""}`}
                    onClick={() => setCatalogTab("treatments")}
                  >
                    Treatments
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={catalogTab === "products"}
                    className={`tabs__item ${catalogTab === "products" ? "tabs__item--active" : ""}`}
                    onClick={() => setCatalogTab("products")}
                  >
                    Products
                  </button>
                </div>
                <div className="search catalog__search">
                  <IconSearch className="search__icon" size={18} stroke={1.75} />
                  <label className="visually-hidden" htmlFor="catalog-search">
                    Search items
                  </label>
                  <input
                    id="catalog-search"
                    className="search__input"
                    type="search"
                    placeholder={`Search ${catalogTab}`}
                    value={catalogQuery}
                    onChange={(e) => setCatalogQuery(e.target.value)}
                  />
                </div>
              </div>
              <div className="catalog__grid">
                {catalog.map((item) => {
                  const outOfStock = catalogTab === "products" && item.stock <= 0;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className="catalog__tile"
                      disabled={outOfStock}
                      onClick={() => addLine(item, catalogTab === "treatments" ? "t" : "r")}
                    >
                      <span className="catalog__name">{item.name}</span>
                      <span className="catalog__meta">
                        <strong>{formatRM(item.price)}</strong>
                        {catalogTab === "products" ? (
                          <span className={item.stock <= 10 ? "catalog__low" : ""}>
                            {outOfStock ? "Out of stock" : `${item.stock} in stock`}
                          </span>
                        ) : (
                          <span>{item.duration} min</span>
                        )}
                      </span>
                    </button>
                  );
                })}
                {catalog.length === 0 && <p className="counter__empty">No {catalogTab} match "{catalogQuery}".</p>}
              </div>
            </div>
          </section>

          {/* Payment */}
          <section className="counter__pay" aria-label="Payment">
            <dl className="totals">
              <div>
                <dt>Subtotal</dt>
                <dd>{formatRM(subtotal)}</dd>
              </div>
              <div className="totals__editable">
                <dt>
                  <label htmlFor="discount">Discount</label>
                </dt>
                <dd>
                  <span className="totals__discount">
                    <input
                      id="discount"
                      className="form-field__control"
                      type="number"
                      min="0"
                      inputMode="decimal"
                      placeholder="0"
                      value={discountInput}
                      onChange={(e) => setDiscountInput(e.target.value)}
                    />
                    <span className="tabs totals__unit">
                      <button
                        type="button"
                        className={`tabs__item ${discountType === "rm" ? "tabs__item--active" : ""}`}
                        aria-pressed={discountType === "rm"}
                        onClick={() => setDiscountType("rm")}
                      >
                        RM
                      </button>
                      <button
                        type="button"
                        className={`tabs__item ${discountType === "pct" ? "tabs__item--active" : ""}`}
                        aria-pressed={discountType === "pct"}
                        onClick={() => setDiscountType("pct")}
                      >
                        %
                      </button>
                    </span>
                  </span>
                  {discount > 0 && <span className="totals__minus">− {formatRM(discount)}</span>}
                </dd>
              </div>
              <div className="totals__editable">
                <dt>
                  <label className="totals__check">
                    <input type="checkbox" checked={panelOn} onChange={(e) => setPanelOn(e.target.checked)} />
                    Panel / insurance
                  </label>
                </dt>
                <dd>{panelAmount > 0 && <span className="totals__minus">− {formatRM(panelAmount)}</span>}</dd>
              </div>
              {panelOn && (
                <div className="totals__panel">
                  <label className="form-field">
                    <span className="visually-hidden">Panel company</span>
                    <select className="form-field__control" value={panelName} onChange={(e) => setPanelName(e.target.value)}>
                      {PANELS.map((p) => (
                        <option key={p}>{p}</option>
                      ))}
                    </select>
                  </label>
                  <label className="form-field">
                    <span className="visually-hidden">Amount covered by panel (RM)</span>
                    <input
                      className="form-field__control"
                      type="number"
                      min="0"
                      inputMode="decimal"
                      placeholder="Covered RM"
                      value={panelInput}
                      onChange={(e) => setPanelInput(e.target.value)}
                    />
                  </label>
                </div>
              )}
              <div className="totals__grand">
                <dt>Patient pays</dt>
                <dd>{formatRM(patientPays)}</dd>
              </div>
            </dl>

            {payments.length > 0 && (
              <ul className="paid-list" aria-label="Payments taken">
                {payments.map((p, i) => (
                  <li key={`${p.method}-${i}`}>
                    <span>
                      {p.method}
                      {p.ref ? ` · ${p.ref}` : ""}
                    </span>
                    <strong>{formatRM(p.amount)}</strong>
                    <button
                      type="button"
                      className="btn btn--ghost btn--icon btn--sm"
                      aria-label={`Remove ${p.method} payment`}
                      onClick={() => {
                        setPayments((list) => list.filter((_, j) => j !== i));
                        setLastChange(0);
                      }}
                    >
                      <IconTrash size={16} stroke={1.75} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {remaining > 0 || patientPays === 0 ? (
              <div className="pay-box">
                <div className="pay-box__due">
                  <span>{payments.length ? "Still due" : "Amount due"}</span>
                  <strong>{formatRM(remaining)}</strong>
                </div>

                {remaining > 0 && (
                  <>
                    <div className="method-grid" role="group" aria-label="Payment method">
                      {PAYMENT_METHODS.map((m) => {
                        const Icon = METHOD_ICONS[m];
                        return (
                          <button
                            key={m}
                            type="button"
                            className={`method-grid__item ${method === m ? "method-grid__item--active" : ""}`}
                            aria-pressed={method === m}
                            onClick={() => {
                              setMethod(m);
                              setError("");
                              if (m === "Cash") setAmountInput("");
                            }}
                          >
                            <Icon size={22} stroke={1.75} />
                            {m}
                          </button>
                        );
                      })}
                    </div>

                    <label className="pay-box__amount">
                      <span>{method === "Cash" ? "Cash received" : "Amount"}</span>
                      <span className="pay-box__display">
                        RM <output aria-live="polite">{amountInput || "0.00"}</output>
                      </span>
                    </label>

                    {method === "Cash" ? (
                      <>
                        <div className="quick-cash">
                          {cashSuggestions.map((v, i) => (
                            <button key={v} type="button" onClick={() => setAmountInput(v.toFixed(2))}>
                              {i === 0 ? "Exact" : `RM ${v % 1 ? v.toFixed(2) : v}`}
                            </button>
                          ))}
                        </div>
                        <div className="keypad" role="group" aria-label="Number pad">
                          {["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "back"].map((k) => (
                            <button key={k} type="button" onClick={() => keypad(k)} aria-label={k === "back" ? "Delete digit" : k}>
                              {k === "back" ? <IconBackspace size={20} stroke={1.75} /> : k}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <label className="form-field">
                        <span className="form-field__label">
                          {method === "Card" ? "Last 4 digits or approval code" : "Reference no."} (optional)
                        </span>
                        <input
                          className="form-field__control"
                          value={reference}
                          onChange={(e) => setReference(e.target.value)}
                          inputMode={method === "Card" ? "numeric" : "text"}
                        />
                      </label>
                    )}

                    {error && (
                      <p className="form-error" role="alert">
                        {error}
                      </p>
                    )}

                    <button className="btn btn--primary pay-box__take" type="button" onClick={takePayment}>
                      Take {method} payment
                    </button>
                    {payments.length > 0 && (
                      <button className="btn btn--ghost btn--sm" type="button" onClick={finish}>
                        Bill the rest later ({formatRM(remaining)})
                      </button>
                    )}
                  </>
                )}
                {patientPays === 0 && cart.length > 0 && (
                  <button className="btn btn--primary pay-box__take" type="button" onClick={finish}>
                    Complete checkout
                  </button>
                )}
              </div>
            ) : (
              <div className="pay-box pay-box--done">
                <IconCircleCheck size={36} stroke={1.75} />
                <strong>Paid in full</strong>
                {lastChange > 0 && (
                  <span className="pay-box__change">
                    Give change <strong>{formatRM(lastChange)}</strong>
                  </span>
                )}
                <button className="btn btn--primary pay-box__take" type="button" onClick={finish}>
                  Complete checkout
                </button>
              </div>
            )}
          </section>
        </>
      )}
      {registerQuery !== null && (
        <NewPatientModal
          title="Register new patient"
          saveLabel="Register & continue"
          initialQuery={registerQuery}
          onClose={() => setRegisterQuery(null)}
          onSaved={(p) => setWalkInPatientId(p.id)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Receipt
// ---------------------------------------------------------------------------

const Receipt = ({ data, onNext }) => {
  const { clinic, today } = useClinicData();
  const { invoice, patient, lines, subtotal, discount, panelAmount, panelName, patientPays, change, balance } = data;
  const phone = patient ? whatsappNumber(patient.phone) : "";
  const message = encodeURIComponent(
    `Receipt ${invoice.id} from ${clinic.name}: ${formatRM(patientPays)}${balance > 0 ? `, ${formatRM(balance)} outstanding` : ", paid in full"}. Thank you!`
  );

  return (
    <section className="counter__receipt" aria-label="Checkout complete">
      <div className="receipt-done checkout-page__no-print">
        <IconCircleCheck size={40} stroke={1.75} />
        <div>
          <h2>{balance > 0 ? "Checkout saved" : "Payment complete"}</h2>
          <p>
            {invoice.id}
            {change > 0 ? `, give change ${formatRM(change)}` : ""}
            {balance > 0 ? `, ${formatRM(balance)} added to the patient's balance` : ""}
          </p>
        </div>
      </div>

      <article className="receipt" aria-label="Receipt">
        <header className="receipt__head">
          <strong>{clinic.name}</strong>
          <span>{clinic.address}</span>
          <span>{formatPhone(clinic.phone, "[Clinic phone]")}</span>
        </header>
        <div className="receipt__meta">
          <span>Receipt {invoice.id}</span>
          <span>{formatDateLong(today)}</span>
          <span>{patient ? patient.name : "Walk-in customer"}</span>
        </div>
        <table className="receipt__lines">
          <tbody>
            {lines.map((l) => (
              <tr key={l.key}>
                <td>
                  {l.name}
                  {l.qty > 1 && <span> × {l.qty}</span>}
                  {l.price !== l.listPrice && (
                    <span className="receipt__adjusted">
                      {formatRM(l.price)} each, usual price {formatRM(l.listPrice)}
                    </span>
                  )}
                </td>
                <td>{formatRM(l.price * l.qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <dl className="receipt__totals">
          <div>
            <dt>Subtotal</dt>
            <dd>{formatRM(subtotal)}</dd>
          </div>
          {discount > 0 && (
            <div>
              <dt>Discount</dt>
              <dd>− {formatRM(discount)}</dd>
            </div>
          )}
          {panelAmount > 0 && (
            <div>
              <dt>{panelName}</dt>
              <dd>− {formatRM(panelAmount)}</dd>
            </div>
          )}
          <div className="receipt__grand">
            <dt>Total</dt>
            <dd>{formatRM(patientPays)}</dd>
          </div>
          {invoice.payments.map((p, i) => (
            <div key={i}>
              <dt>Paid by {p.method}</dt>
              <dd>{formatRM(p.amount)}</dd>
            </div>
          ))}
          {change > 0 && (
            <div>
              <dt>Change</dt>
              <dd>{formatRM(change)}</dd>
            </div>
          )}
          {balance > 0 && (
            <div className="receipt__balance">
              <dt>Balance due</dt>
              <dd>{formatRM(balance)}</dd>
            </div>
          )}
        </dl>
        <footer className="receipt__foot">Thank you. Please keep this receipt for your records.</footer>
      </article>

      <div className="receipt-actions checkout-page__no-print">
        <button className="btn btn--secondary" type="button" onClick={() => window.print()}>
          <IconPrinter size={18} stroke={1.75} />
          Print
        </button>
        {patient && (
          <a
            className="btn btn--secondary"
            href={`https://wa.me/${phone}?text=${message}`}
            target="_blank"
            rel="noreferrer"
          >
            <IconBrandWhatsapp size={18} stroke={1.75} />
            WhatsApp
          </a>
        )}
        {patient?.email && (
          <a className="btn btn--secondary" href={`mailto:${patient.email}?subject=Receipt ${invoice.id}&body=${message}`}>
            <IconMail size={18} stroke={1.75} />
            Email
          </a>
        )}
        <button className="btn btn--primary" type="button" onClick={onNext}>
          Next patient
        </button>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Day closing
// ---------------------------------------------------------------------------

const DayClosing = () => {
  const { today, invoices, patientById, itemName } = useClinicData();
  const [float, setFloat] = useState("200");
  const [counted, setCounted] = useState("");
  const [closedAt, setClosedAt] = useState(null);

  const todays = invoices.filter((inv) => inv.date === today);
  const payments = todays.flatMap((inv) =>
    (inv.payments || (inv.paid ? [{ method: inv.method || "Cash", amount: inv.paid }] : [])).map((p) => ({ ...p, invoice: inv }))
  );
  const byMethod = PAYMENT_METHODS.map((m) => ({
    method: m,
    total: payments.filter((p) => p.method === m).reduce((s, p) => s + p.amount, 0),
    count: payments.filter((p) => p.method === m).length,
  }));
  const panelClaims = todays.reduce((s, inv) => s + (inv.panel?.amount || 0), 0);
  const collected = payments.reduce((s, p) => s + p.amount, 0);

  const cashSales = byMethod.find((m) => m.method === "Cash").total;
  const expectedCash = (Number(float) || 0) + cashSales;
  const difference = counted === "" ? null : round2((Number(counted) || 0) - expectedCash);

  return (
    <div className="closing">
      <section className="closing__summary" aria-label="Collected today by method">
        {byMethod.map((m) => {
          const Icon = METHOD_ICONS[m.method];
          return (
            <div className="closing__figure" key={m.method}>
              <span className="closing__label">
                <Icon size={18} stroke={1.75} />
                {m.method}
              </span>
              <strong>{formatRM(m.total)}</strong>
              <span>{m.count === 1 ? "1 payment" : `${m.count} payments`}</span>
            </div>
          );
        })}
        <div className="closing__figure">
          <span className="closing__label">Panel claims</span>
          <strong>{formatRM(panelClaims)}</strong>
          <span>To claim from panels</span>
        </div>
      </section>

      <div className="closing__grid">
        <section className="panel panel--flush" aria-labelledby="tx-title">
          <div className="panel__header closing__panel-head">
            <div>
              <h2 className="panel__title" id="tx-title">
                Today's payments
              </h2>
              <p className="panel__subtitle">
                {formatDateLong(today)}, {formatRM(collected)} collected
              </p>
            </div>
          </div>
          <div className="data-table__wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Receipt</th>
                  <th>Patient</th>
                  <th>Method</th>
                  <th className="data-table__num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p, i) => (
                  <tr key={`${p.invoice.id}-${i}`}>
                    <td>
                      <span className="data-table__primary">{p.invoice.id}</span>
                      <span className="data-table__secondary">{p.invoice.items.map(itemName).join(", ")}</span>
                    </td>
                    <td>{patientById[p.invoice.patientId]?.name || "Walk-in customer"}</td>
                    <td className="data-table__nowrap">
                      {p.method}
                      {p.ref && <span className="data-table__secondary">Ref {p.ref}</span>}
                    </td>
                    <td className="data-table__num">{formatRM(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {payments.length === 0 && (
              <div className="empty-state">
                <p className="empty-state__title">No payments yet today</p>
                <p>Payments taken at the counter will appear here.</p>
              </div>
            )}
          </div>
        </section>

        <section className="panel closing__cash" aria-labelledby="cash-title">
          <h2 className="panel__title" id="cash-title">
            Count the cash drawer
          </h2>
          <label className="form-field">
            <span className="form-field__label">Opening float (RM)</span>
            <input
              className="form-field__control"
              type="number"
              min="0"
              inputMode="decimal"
              value={float}
              onChange={(e) => setFloat(e.target.value)}
              disabled={!!closedAt}
            />
          </label>
          <dl className="closing__calc">
            <div>
              <dt>Cash payments</dt>
              <dd>{formatRM(cashSales)}</dd>
            </div>
            <div className="closing__expected">
              <dt>Expected in drawer</dt>
              <dd>{formatRM(expectedCash)}</dd>
            </div>
          </dl>
          <label className="form-field">
            <span className="form-field__label">Cash counted (RM)</span>
            <input
              className="form-field__control closing__count"
              type="number"
              min="0"
              inputMode="decimal"
              value={counted}
              onChange={(e) => setCounted(e.target.value)}
              disabled={!!closedAt}
            />
          </label>
          {difference !== null && (
            <p
              className={`closing__diff ${
                difference === 0 ? "closing__diff--ok" : difference > 0 ? "closing__diff--over" : "closing__diff--short"
              }`}
              role="status"
            >
              {difference === 0
                ? "Drawer balances."
                : difference > 0
                ? `Over by ${formatRM(difference)}`
                : `Short by ${formatRM(Math.abs(difference))}`}
            </p>
          )}
          {closedAt ? (
            <p className="closing__closed">
              <IconCircleCheck size={18} stroke={2} />
              Day closed at {closedAt}
            </p>
          ) : (
            <button
              className="btn btn--primary"
              type="button"
              disabled={counted === ""}
              onClick={() =>
                setClosedAt(new Date().toLocaleTimeString("en-MY", { hour: "numeric", minute: "2-digit" }))
              }
            >
              Close the day
            </button>
          )}
        </section>
      </div>
    </div>
  );
};

export default CheckoutPage;
